import React, { useState } from 'react';

import classnames from 'classnames';

import { useDeleteFileByIdMutation } from '@school_ify/schoolify-repository/src/shared/types';

import { randString } from 'clean/utils/functions';
import CheckBox from 'clean/components/common/CheckBox/CheckBox';
import BinTrash from 'components/Icons/BinTrash';
import { SERVICES_ENDPOINT } from 'utils/constants';

import DropZone from './partials/DropZone/DropZone';
import { convertMimeIntoComponent } from './utils';

import './FileUploader.scoped.scss';

// Reusable file uploader, consists of drag zone and list of files

// Example:

//  <FileUploader
//    readOnly                                // If true, wll remove ability to upload files and will only show list of files
//    fileListTitle="My files"                // Title for file list
//    files={files}                           // List of files (IFileInterface)
//    setFiles={setFiles}                     // Chnage handler, supplies new list of files
//    onDelete={onDelete}                     // Deletion handler, supplies list of file ids, that has been removed
//    accept={['application/pdf']}            // Array of acceptable mime types (use only specific mime types here)
//  />

interface IFileInterface {
  id: string;
  name: string;
  mimeType: string;
  instance?: File;
  path?: string;
  clickHandler?: () => void;
}

interface Props {
  files: IFileInterface[];
  setFiles?: React.Dispatch<React.SetStateAction<IFileInterface[]>>;
  onDelete?: (arg: string[]) => void;
  fileListTitle?: string;
  readOnly?: boolean;
  accept?: string[];
}

const FileUploader: React.FC<Props> = ({
  readOnly = false,
  accept,
  files,
  setFiles,
  // I need this to avoid checking for fn existance
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDelete = () => {},
  fileListTitle = '',
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const [deleteFileById] = useDeleteFileByIdMutation();

  const hanldeUploadChange = fileList => {
    setFiles(files => [
      ...files,
      ...[...fileList].map(instance => {
        if (!instance?.id) {
          Object.defineProperty(instance, 'id', {
            value: randString(),
            writable: false,
          });
        }
        Object.defineProperty(instance, 'needsUpload', {
          value: true,
          writable: false,
        });
        return instance;
      }),
    ]);
  };

  const readFile = async (id: string) => {
    if (!id) return;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: `{"id":"${id}"}`,
    };
    fetch(`${SERVICES_ENDPOINT}/api/file/read`, options)
      .then(response => response.json())
      .then(response => {
        if (response?.value) {
          window.open(response?.value, '_blank', 'noopener,noreferrer');
        }
      });
  };

  const handleDeletion = () => {
    if (selectedFiles.length > 0) {
      selectedFiles.forEach(file => {
        if (file.size === 0) {
          deleteFileById({ variables: { id: file.id } });
        }
      });
      setFiles(files =>
        files.filter(({ id }) => selectedFiles.map(file => file.id).indexOf(id) === -1)
      );
      onDelete(selectedFiles);
      setSelectedFiles([]);
    }
  };

  const handleSelecting = id => () => {
    setSelectedFiles(selectedFiles => [
      ...selectedFiles.filter(file => file.id !== id),
      ...(selectedFiles.indexOf(id) === -1 ? [files.find(file => file.id === id)] : []),
    ]);
  };

  return (
    <div className="file-uploader">
      {!readOnly && <DropZone accept={accept} onChange={hanldeUploadChange} />}

      {files.length > 0 && (
        <div className="file-list">
          <h3 className="title">{fileListTitle}</h3>
          {!readOnly && (
            <div
              className={classnames({ delete: true, disabled: selectedFiles.length === 0 })}
              onClick={handleDeletion}
            >
              <BinTrash />
            </div>
          )}
          <ul className="list">
            {
              // I need this to avoid checking for fn existance
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              files.map(({ id, name, mimeType }) => {
                return (
                  <li key={id} className="file">
                    {!readOnly && (
                      <CheckBox
                        round
                        onChange={handleSelecting(id)}
                        checked={selectedFiles.map(file => file.id).indexOf(id) !== -1}
                      />
                    )}
                    <span onClick={() => readFile(id)} className="file pointer">
                      {convertMimeIntoComponent(mimeType)} <span className="name">{name}</span>
                    </span>
                  </li>
                );
              })
            }
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUploader;
